import { type ApiErrorBody, httpFailure } from '@shared/errors'
import type {
  ImageAssetReference,
  ImageOriginalUrlResponse,
  ImagePreviewBatchResponse,
  ImagePreviewUrls
} from '@shared/image-assets'
import type {
  AdminVideoMetrics,
  CharacterCardGenerationRequest,
  CharacterCardSelectionRequest,
  CharacterCardSelectionResponse,
  CharacterCardUploadRequest,
  CharacterCardUploadResponse,
  CharacterMutationResponse,
  CharacterPromptHelperRequest,
  CharacterPromptHelperResponse,
  CharacterSource,
  CharacterStatus,
  CharacterVisibility,
  CreateCharacterRequest,
  CreateManagedUserRequest,
  CreateManagedUserResponse,
  CreateVideoDraftRequest,
  CreateVideoDraftResponse,
  DeleteCharacterResponse,
  DeleteManagedUserRequest,
  DynamicCharacterDefinition,
  EnrichmentPromptTemplateConfig,
  ListCharactersResponse,
  ListManagedUsersResponse,
  ManagedUser,
  ResendManagedUserInvitationRequest,
  RunwayCreditsResponse,
  SaveAdminGeminiSettingsRequest,
  UpdateCharacterRequest,
  VideoGeneration,
  VideoGenerationHistoryResponse,
  VideoGenerationStatus,
  VideoInputDescriptor
} from '@shared/index'
import type {
  CharacterReferenceAssets,
  CreateReferenceBuilderRunRequest,
  PlaygroundHistoryScope,
  ReferenceAssetSelection
} from '@shared/reference-assets'
import type {
  Character,
  CostTrackingBreakdownBucket,
  CostTrackingBucket,
  CostTrackingMetrics,
  GeminiEnrichmentSettings,
  GeminiImageGenerationSettings,
  GeminiImageSettings,
  GeminiImageSettingsSource,
  OutpaintInput,
  PlaygroundJob,
  PlaygroundRun,
  PlaygroundToolType,
  UploadPurpose,
  UpscaleInput,
  WorkflowAsset,
  WorkflowBatch,
  WorkflowFolder,
  WorkflowHistoryItem,
  WorkflowRun,
  WorkflowV2Stage,
  WorkflowWorkspaceResponse
} from '@shared/types'

import { getApiToken } from '@/lib/auth/config'
import { type AppError, createAppError, isAbortError, safeRequestId } from '@/lib/errors'
import type { PlaygroundHistoryItem } from '@/lib/queries/usePlayground'

const API_URL = import.meta.env.VITE_API_URL ?? ''

export type ApiError = AppError & { status: number }

type JsonRequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
}

type GeminiImageModelOption = {
  value: GeminiImageSettings['model']
  label: string
  description?: string
}

type GeminiEnrichmentModelOption = {
  value: GeminiEnrichmentSettings['model']
  label: string
  description?: string
}

// Backend accepts either section independently (union schema) — send only the
// sections being saved so untouched settings are preserved server-side.
export type SaveAdminGeminiSettingsPayload = SaveAdminGeminiSettingsRequest

function toRequestUrl(path: string): string {
  return path.startsWith('http') ? path : `${API_URL}${path}`
}

async function createJsonRequestInit(options?: JsonRequestOptions): Promise<RequestInit> {
  const headers = new Headers(options?.headers)
  const hasBody = Object.prototype.hasOwnProperty.call(options ?? {}, 'body')
  const token = await getApiToken()

  if (token) headers.set('Authorization', `Bearer ${token}`)

  if (hasBody) {
    headers.set('Content-Type', 'application/json')
  }

  return {
    ...options,
    headers,
    body: hasBody ? JSON.stringify(options?.body) : undefined
  }
}

async function createApiError(response: Response, fallbackMessage: string): Promise<ApiError> {
  const failure = httpFailure(response.status)
  let body: ApiErrorBody = {}
  try {
    const parsed: unknown = await response.json()
    if (parsed && typeof parsed === 'object') body = parsed
  } catch {
    // Gateways may return HTML or an empty response. Status still provides a useful reason.
  }
  const specificMessage =
    typeof body.message === 'string' ? body.message : typeof body.error === 'string' ? body.error : undefined
  // A code other than the status default means the handler explained this failure itself (for
  // example, a character that is not available), so its message wins even for a 403 or 404.
  const hasSpecificCode = typeof body.code === 'string' && body.code !== failure.code
  const useSpecificMessage =
    response.status < 500 &&
    (hasSpecificCode ||
      (![401, 403, 404, 429].includes(response.status) &&
        !['Bad Request', 'Conflict', 'Not Found', 'Forbidden', 'Unauthorized'].includes(specificMessage ?? '')))
  // API Gateway's own ID appears in its access logs beside the Lambda request ID, so it traces the
  // whole request. The body's reference is the fallback for responses that bypass the gateway.
  const requestId = safeRequestId(response.headers.get('apigw-requestid')) ?? safeRequestId(body.requestId)
  const detail = useSpecificMessage && specificMessage ? specificMessage : `${fallbackMessage}. ${failure.message}`
  return createAppError(`${detail}${requestId ? ` Reference: ${requestId}.` : ''}`, {
    detail,
    status: response.status,
    code: typeof body.code === 'string' ? body.code : failure.code,
    requestId,
    operation: fallbackMessage,
    fieldErrors:
      body.fieldErrors && typeof body.fieldErrors === 'object'
        ? Object.fromEntries(Object.entries(body.fieldErrors).filter(([, value]) => typeof value === 'string'))
        : undefined
  }) as ApiError
}

async function requestResponse(path: string, options: JsonRequestOptions | undefined, operation: string) {
  try {
    return await fetch(toRequestUrl(path), await createJsonRequestInit(options))
  } catch (cause) {
    if (isAbortError(cause)) throw cause
    throw createAppError(
      `${operation}. The service could not be reached. Check your connection before trying again. If you submitted a generation, check History before resubmitting.`,
      {
        code: 'NETWORK_ERROR',
        operation
      }
    )
  }
}

async function requestJson<T>(
  path: string,
  options?: JsonRequestOptions,
  fallbackMessage = 'Request failed'
): Promise<T> {
  const response = await requestResponse(path, options, fallbackMessage)

  if (!response.ok) {
    throw await createApiError(response, fallbackMessage)
  }

  try {
    return (await response.json()) as T
  } catch {
    throw createAppError(
      `${fallbackMessage}. The service returned an unreadable response. Refresh the item before trying again.`,
      { code: 'INVALID_RESPONSE', operation: fallbackMessage }
    )
  }
}

async function requestVoid(
  path: string,
  options?: JsonRequestOptions,
  fallbackMessage = 'Request failed'
): Promise<void> {
  const response = await requestResponse(path, options, fallbackMessage)

  if (!response.ok) {
    throw await createApiError(response, fallbackMessage)
  }
}

/** These responses feed the observer above the router's render boundary. */
function requireReadableGeneration(valid: boolean) {
  if (!valid)
    throw createAppError('The service returned incomplete generation status. Try loading it again.', {
      code: 'INVALID_RESPONSE',
      operation: 'Read generation status'
    })
}

// Admin config response type
export type AdminCharacterConfig = EnrichmentPromptTemplateConfig & {
  characterSheetImageKey?: string
  scaleReferenceBriefs?: Record<string, string>
  character: Character
  displayName: string
  source: CharacterSource
  status: CharacterStatus
  /** Present only for dynamic characters. */
  definition?: DynamicCharacterDefinition
  modelName: string
  geminiImageConfig: GeminiImageSettings
  geminiImageConfigSource: GeminiImageSettingsSource
  geminiImageCodeDefault: GeminiImageSettings
  geminiImageOptions: {
    models: GeminiImageModelOption[]
    temperature: {
      min: number
      max: number
      step: number
    }
    aspectRatios: GeminiImageSettings['aspectRatio'][]
    imageSizes: GeminiImageSettings['imageSize'][]
  }
  geminiEnrichmentConfig: GeminiEnrichmentSettings
  geminiEnrichmentConfigSource: GeminiImageSettingsSource
  geminiEnrichmentCodeDefault: GeminiEnrichmentSettings
  geminiEnrichmentOptions: {
    models: GeminiEnrichmentModelOption[]
  }
  allowedTools: PlaygroundToolType[]
  referenceImages: { key: string }[]
  /** `bundled` means the deploy-seeded principal copies are in use because nothing was uploaded. */
  referenceImagesSource: 'custom' | 'bundled' | 'none'
  scaleReferenceImageKey?: string
  scaleReferenceImageKeys?: string[]
  guidelinesUrl?: string
  /** Stored per stage for principal and dynamic characters. */
  visibility: CharacterVisibility
}

export type GeminiImageDefaults = {
  scaleReferenceImageCount?: number
  character: Character
  effectiveSettings: GeminiImageSettings
  source: GeminiImageSettingsSource
  codeDefault: GeminiImageSettings
  guidelinesUrl?: string
  effectiveEnrichmentSettings: GeminiEnrichmentSettings
  enrichmentSource: GeminiImageSettingsSource
  enrichmentCodeDefault: GeminiEnrichmentSettings
  options: {
    models: GeminiImageModelOption[]
    temperature: {
      min: number
      max: number
      step: number
    }
    aspectRatios: GeminiImageSettings['aspectRatio'][]
    imageSizes: GeminiImageSettings['imageSize'][]
  }
  enrichmentOptions: {
    models: GeminiEnrichmentModelOption[]
  }
}

// Playground status response — extends PlaygroundJob with computed cost.
export interface PlaygroundStatusResponse extends PlaygroundJob {
  geminiCallCount?: number
  estimatedCostUsd?: number
}

export interface PlaygroundRunResponse {
  run: PlaygroundRun
  assets: PlaygroundStatusResponse[]
}

type CreatePlaygroundImageRunBase = ReferenceAssetSelection & {
  useScaleReferences?: boolean
  runId: string
  experimentId?: string
  parentRunId?: string
  character?: Character
  maskImageKey?: string
  backgroundImageKey?: string
  outpaintLayoutGuideImageKey?: string
  outpaint?: OutpaintInput
  referenceImageKeys?: string[]
  skipEnrichment?: boolean
  enrichedPrompt?: string
  geminiImageSettings?: GeminiImageGenerationSettings
}

export type CreatePlaygroundImageRunRequest =
  | (Pick<
      CreatePlaygroundImageRunBase,
      'runId' | 'experimentId' | 'parentRunId' | 'referenceImageKeys' | 'geminiImageSettings'
    > & {
      toolType: 'freeflow'
      variantCount: 1 | 2 | 3 | 4
      userInput: string
      startingFrameJobId?: string
    })
  | (CreatePlaygroundImageRunBase & {
      toolType: 'background'
      variantCount: 1 | 2 | 3 | 4
      userInput: string
      inputImageKey?: string
    })
  | (CreatePlaygroundImageRunBase & {
      toolType: 'inpaint' | 'composition' | 'outpaint'
      variantCount: 1 | 2 | 3 | 4
      userInput: string
      inputImageKey: string
    })
  | (CreatePlaygroundImageRunBase & {
      toolType: 'upscale'
      variantCount: 1
      upscale: UpscaleInput
      inputImageKey: string
      userInput?: never
      geminiImageSettings?: never
    })

export type CreateWorkflowRequest = ReferenceAssetSelection & {
  character: Character
  /** Required only when starting by generating a character. */
  prompt?: string
  variantCount?: 1 | 2 | 3 | 4
  folderId?: string
  /**
   * User-owned upload shortcuts. Valid sets are character, character plus
   * background, or composition by itself.
   */
  initialUploads?: Array<{ stage: 'character' | 'background' | 'composition'; imageKey: string }>
  referenceImageKeys?: string[]
  skipEnrichment?: boolean
  enrichedPrompt?: string
  geminiImageSettings?: GeminiImageGenerationSettings
  /**
   * A UUID chosen per creation. Sending the same request with the same key
   * returns the workflow the first attempt created instead of creating another.
   */
  idempotencyKey?: string
}

export type SubmitWorkflowRunRequest = ReferenceAssetSelection & {
  useScaleReferences?: boolean
  workflowId: string
  stage: WorkflowV2Stage
  sourceAssetIds: string[]
  prompt?: string
  variantCount: 1 | 2 | 3 | 4
  referenceImageKeys?: string[]
  maskImageKey?: string
  outpaintLayoutGuideImageKey?: string
  outpaint?: OutpaintInput
  upscale?: UpscaleInput
  skipEnrichment?: boolean
  enrichedPrompt?: string
  geminiImageSettings?: GeminiImageGenerationSettings
  /** The failed batch this submission retries. */
  retryOfBatchId?: string
  /**
   * A UUID chosen per submission. Sending the same request with the same key
   * returns the batch the first attempt created instead of creating another.
   */
  idempotencyKey?: string
}

export type WorkflowRunSubmission = { batch: WorkflowBatch; runs: WorkflowRun[]; assets: WorkflowAsset[] }

export type PlaygroundHistoryFeedEntry =
  { kind: 'legacy'; job: PlaygroundHistoryItem } | { kind: 'run'; run: PlaygroundRun; assets: PlaygroundHistoryItem[] }

// The cost-tracking response contract lives in shared; re-exported here for existing imports.
export type { CostTrackingBreakdownBucket, CostTrackingBucket, CostTrackingMetrics }

export interface EC2CapacityStatus {
  status: 'ready' | 'starting' | 'stopped' | 'stopping' | 'capacity_full' | 'unknown'
  message: string
  ec2State: string
}

export const api = {
  getEC2Capacity: async (): Promise<EC2CapacityStatus> =>
    requestJson<EC2CapacityStatus>('/api/ec2/capacity', undefined, 'Failed to fetch EC2 capacity'),

  submitPlaygroundCharacter: async (data: { userInput: string; jobId?: string }): Promise<{ jobId: string }> =>
    requestJson<{ jobId: string }>(
      '/api/steps/character',
      { method: 'POST', body: data },
      'Failed to submit EC2 Playground character generation'
    ),

  getPlaygroundStatus: async (jobId: string): Promise<PlaygroundStatusResponse> => {
    const response = await requestJson<PlaygroundStatusResponse>(
      `/api/playground/status/${jobId}`,
      undefined,
      'Failed to fetch playground status'
    )
    requireReadableGeneration(typeof response?.jobId === 'string' && typeof response.status === 'string')
    return response
  },

  createWorkflow: async (
    data: CreateWorkflowRequest
  ): Promise<WorkflowRunSubmission & Pick<WorkflowWorkspaceResponse, 'workflow'>> =>
    requestJson<WorkflowRunSubmission & Pick<WorkflowWorkspaceResponse, 'workflow'>>(
      '/api/workflows',
      { method: 'POST', body: data },
      'Failed to create workflow'
    ),

  getWorkflow: async (workflowId: string): Promise<WorkflowWorkspaceResponse> => {
    const response = await requestJson<WorkflowWorkspaceResponse>(
      `/api/workflows/${workflowId}`,
      undefined,
      'Failed to load workflow'
    )
    requireReadableGeneration(
      typeof response?.workflow?.workflowId === 'string' &&
        Array.isArray(response.runs) &&
        Array.isArray(response.assets)
    )
    return response
  },

  deleteWorkflow: async (workflowId: string): Promise<void> =>
    requestJson<void>(`/api/workflows/${workflowId}`, { method: 'DELETE' }, 'Failed to delete workflow'),

  getWorkflows: async (creator?: string): Promise<{ workflows: WorkflowHistoryItem[] }> =>
    requestJson<{ workflows: WorkflowHistoryItem[] }>(
      `/api/workflows${creator ? `?creator=${encodeURIComponent(creator)}` : ''}`,
      undefined,
      'Failed to load workflow history'
    ),

  submitWorkflowRun: async (data: SubmitWorkflowRunRequest): Promise<WorkflowRunSubmission> =>
    requestJson<WorkflowRunSubmission>(
      `/api/workflows/${data.workflowId}/runs`,
      { method: 'POST', body: { ...data, workflowId: undefined } },
      'Failed to submit workflow generation'
    ),

  getFolders: async (): Promise<{ folders: WorkflowFolder[] }> =>
    requestJson<{ folders: WorkflowFolder[] }>('/api/folders', undefined, 'Failed to fetch folders'),

  getFolder: async (folderId: string): Promise<{ folder: WorkflowFolder }> =>
    requestJson<{ folder: WorkflowFolder }>(`/api/folders/${folderId}`, undefined, 'Failed to fetch folder'),

  createFolder: async (name: string): Promise<{ folder: WorkflowFolder }> =>
    requestJson<{ folder: WorkflowFolder }>(
      '/api/folders',
      { method: 'POST', body: { name } },
      'Failed to create folder'
    ),

  renameFolder: async (folderId: string, name: string): Promise<{ folder: WorkflowFolder }> =>
    requestJson<{ folder: WorkflowFolder }>(
      `/api/folders/${folderId}`,
      { method: 'PATCH', body: { name } },
      'Failed to rename folder'
    ),

  // Deletes the folder only — its workflows are unassigned server-side, never deleted.
  deleteFolder: async (folderId: string): Promise<{ folderId: string; unassignedWorkflows: number }> =>
    requestJson<{ folderId: string; unassignedWorkflows: number }>(
      `/api/folders/${folderId}`,
      { method: 'DELETE' },
      'Failed to delete folder'
    ),

  getGeminiImageDefaults: async (character: Character): Promise<GeminiImageDefaults> =>
    requestJson<GeminiImageDefaults>(
      `/api/config/${character}/gemini-defaults`,
      undefined,
      'Failed to fetch Gemini defaults'
    ),

  getPlaygroundHistoryFeed: async (
    creator?: string,
    scope?: PlaygroundHistoryScope
  ): Promise<{ entries: PlaygroundHistoryFeedEntry[] }> => {
    const params = new URLSearchParams()
    if (creator) params.set('creator', creator)
    if (scope) params.set('scope', scope)
    return requestJson<{ entries: PlaygroundHistoryFeedEntry[] }>(
      `/api/playground/history-feed${params.size ? `?${params}` : ''}`,
      undefined,
      'Failed to fetch generation history'
    )
  },

  getPlaygroundRun: async (runId: string, includeDebug = false): Promise<PlaygroundRunResponse> => {
    const response = await requestJson<PlaygroundRunResponse>(
      `/api/playground/runs/${runId}${includeDebug ? '?includeDebug=true' : ''}`,
      undefined,
      'Failed to fetch Playground run'
    )
    requireReadableGeneration(
      typeof response?.run?.runId === 'string' &&
        typeof response.run.status === 'string' &&
        Array.isArray(response.assets)
    )
    return response
  },

  createCharacterNanobananaRun: async (
    data: ReferenceAssetSelection & {
      runId: string
      experimentId?: string
      parentRunId?: string
      variantCount: 1 | 2 | 3 | 4
      userInput: string
      character: Character
      referenceImageKeys?: string[]
      skipEnrichment?: boolean
      enrichedPrompt?: string
      geminiImageSettings?: GeminiImageGenerationSettings
    }
  ): Promise<PlaygroundRunResponse> =>
    requestJson<PlaygroundRunResponse>(
      '/api/playground/character-nanobanana/runs',
      { method: 'POST', body: data },
      'Failed to submit Nano Banana generation run'
    ),

  createPlaygroundImageRun: async (data: CreatePlaygroundImageRunRequest): Promise<PlaygroundRunResponse> =>
    requestJson<PlaygroundRunResponse>(
      `/api/playground/${data.toolType}/runs`,
      { method: 'POST', body: data },
      `Failed to submit ${data.toolType} generation run`
    ),

  getImagePreviews: async (
    assets: ImageAssetReference[],
    character?: Character
  ): Promise<Record<string, ImagePreviewUrls>> => {
    if (assets.length === 0) return {}
    const response = await requestJson<ImagePreviewBatchResponse>(
      '/api/images/previews',
      { method: 'POST', body: { assets, ...(character && { character }) } },
      'Failed to load image previews'
    )
    return response.urls
  },

  getOriginalImageUrl: (asset: ImageAssetReference, disposition: 'inline' | 'attachment') =>
    requestJson<ImageOriginalUrlResponse>(
      '/api/images/original-url',
      { method: 'POST', body: { asset, disposition } },
      'Failed to prepare original image'
    ),

  getReferenceAssets: (character: Character) =>
    requestJson<CharacterReferenceAssets>(
      `/api/characters/${encodeURIComponent(character)}/reference-assets`,
      undefined,
      'Failed to load reference assets'
    ),
  createReferenceBuilderRun: (data: CreateReferenceBuilderRunRequest) =>
    requestJson<PlaygroundRunResponse>(
      '/api/playground/reference-builder/runs',
      { method: 'POST', body: data },
      'Failed to generate references'
    ),
  saveCharacterSheet: (character: Character, key: string | null) =>
    requestJson<{ key: string | null }>(
      `/api/admin/characters/${encodeURIComponent(character)}/reference-assets`,
      { method: 'PATCH', body: { characterSheetImageKey: key } },
      'Failed to save character sheet'
    ),

  checkHealth: async (): Promise<{ status: string }> =>
    requestJson<{ status: string }>('/api/health', undefined, 'Health check failed'),

  enhancePrompt: async (
    prompt: string,
    type: 'character-wan' | 'character-nanobanana' | 'generic' | 'inpaint' = 'generic',
    character?: Character,
    referenceImageKeys?: string[],
    options?: {
      persistToPlaygroundHistory?: boolean
      playgroundJobId?: string
    }
  ): Promise<{ enrichedPrompt: string; jobId?: string }> => {
    return requestJson<{ enrichedPrompt: string; jobId?: string }>(
      '/api/steps/enhance-prompt',
      {
        method: 'POST',
        body: {
          prompt,
          type,
          ...(character && { character }),
          ...(referenceImageKeys?.length && { referenceImageKeys }),
          ...(options?.persistToPlaygroundHistory && { persistToPlaygroundHistory: true }),
          ...(options?.playgroundJobId && { playgroundJobId: options.playgroundJobId })
        }
      },
      'Failed to enhance prompt'
    )
  },

  submitRemoveBackgroundGeneration: async (data: {
    imageKey: string // S3 key from presigned upload (required)
    jobId?: string
    geminiImageSettings?: GeminiImageGenerationSettings
  }): Promise<{ jobId: string; status: string; message: string }> =>
    requestJson<{ jobId: string; status: string; message: string }>(
      '/api/steps/remove-background',
      { method: 'POST', body: data },
      'Failed to submit background removal'
    ),

  // Get presigned URL for direct S3 upload
  getPresignedUploadUrl: async (data: {
    contentType: string
    fileSize: number
    purpose?: UploadPurpose
  }): Promise<{ uploadUrl: string; key: string; bucket: string; expiresIn: number }> =>
    requestJson<{ uploadUrl: string; key: string; bucket: string; expiresIn: number }>(
      '/api/upload/presigned',
      { method: 'POST', body: data },
      'Failed to get presigned URL'
    ),

  // Upload file directly to S3 using presigned URL
  uploadToS3: async (uploadUrl: string, file: Blob, contentType: string): Promise<void> => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType
      },
      body: file
    })

    if (!response.ok) {
      throw createAppError(
        response.status === 403
          ? 'The upload link expired or was rejected. Try uploading the image again.'
          : 'The image could not be uploaded. Check your connection and try again.',
        { code: 'UPLOAD_FAILED', status: response.status }
      )
    }
  },

  // Delete playground job
  deletePlaygroundJob: async (jobId: string): Promise<void> =>
    requestVoid(`/api/playground/${jobId}`, { method: 'DELETE' }, 'Failed to delete playground job'),

  deletePlaygroundRun: async (runId: string): Promise<void> =>
    requestVoid(`/api/playground/runs/${runId}`, { method: 'DELETE' }, 'Failed to delete Playground run'),

  // Get admin character config
  getAdminCharacterConfig: async (character: Character): Promise<AdminCharacterConfig> =>
    requestJson<AdminCharacterConfig>(`/api/admin/config/${character}`, undefined, 'Failed to fetch admin config'),

  saveAdminGeminiSettings: async (character: Character, settings: SaveAdminGeminiSettingsPayload): Promise<void> =>
    requestVoid(
      `/api/admin/config/${character}/gemini-settings`,
      { method: 'POST', body: settings },
      'Failed to save Gemini settings'
    ),

  resetAdminGeminiSettings: async (character: Character): Promise<void> =>
    requestVoid(
      `/api/admin/config/${character}/gemini-settings`,
      { method: 'DELETE' },
      'Failed to reset Gemini settings'
    ),

  getCostTrackingMetrics: async (character: Character): Promise<CostTrackingMetrics> =>
    requestJson<CostTrackingMetrics>(
      `/api/admin/cost-tracking/${character}`,
      undefined,
      'Failed to fetch cost tracking metrics'
    ),

  getAdminVideoMetrics: async (): Promise<AdminVideoMetrics> =>
    requestJson<AdminVideoMetrics>('/api/admin/video-metrics', undefined, 'Failed to fetch video metrics'),

  getManagedUsers: async (): Promise<ManagedUser[]> => {
    const response = await requestJson<ListManagedUsersResponse>('/api/admin/users', undefined, 'Failed to load users')
    return response.users
  },

  createManagedUser: async (data: CreateManagedUserRequest): Promise<ManagedUser> => {
    const response = await requestJson<CreateManagedUserResponse>(
      '/api/admin/users',
      { method: 'POST', body: data },
      'Failed to invite user'
    )
    return response.user
  },

  deleteManagedUser: async (data: DeleteManagedUserRequest): Promise<void> =>
    requestVoid('/api/admin/users', { method: 'DELETE', body: data }, 'Failed to delete user'),

  resendManagedUserInvitation: async (data: ResendManagedUserInvitationRequest): Promise<void> =>
    requestVoid('/api/admin/users/resend-invitation', { method: 'POST', body: data }, 'Failed to resend invitation'),

  // Delete admin reference image
  deleteAdminReferenceImage: async (character: Character, key: string): Promise<void> =>
    requestVoid(
      `/api/admin/config/${character}/reference`,
      { method: 'DELETE', body: { key } },
      'Failed to delete reference image'
    ),

  // Character catalog: principal + dynamic characters visible in this stage
  getCharacters: async (): Promise<ListCharactersResponse> =>
    requestJson<ListCharactersResponse>('/api/characters', undefined, 'Failed to load characters'),

  createCharacter: async (data: CreateCharacterRequest): Promise<CharacterMutationResponse> =>
    requestJson<CharacterMutationResponse>(
      '/api/admin/characters',
      { method: 'POST', body: data },
      'Failed to create character'
    ),

  updateCharacter: async (character: Character, data: UpdateCharacterRequest): Promise<CharacterMutationResponse> =>
    requestJson<CharacterMutationResponse>(
      `/api/admin/characters/${character}`,
      { method: 'PATCH', body: data },
      'Failed to update character'
    ),

  deleteCharacter: async (character: Character): Promise<DeleteCharacterResponse> =>
    requestJson<DeleteCharacterResponse>(
      `/api/admin/characters/${character}`,
      { method: 'DELETE' },
      'Failed to delete character'
    ),

  // Character Onboarding helpers
  getCharacterCardUploadUrl: async (data: CharacterCardUploadRequest): Promise<CharacterCardUploadResponse> =>
    requestJson<CharacterCardUploadResponse>(
      '/api/character-onboarding/card-image',
      { method: 'POST', body: data },
      'Failed to get card upload URL'
    ),

  generateCharacterCard: async (data: CharacterCardGenerationRequest): Promise<PlaygroundRunResponse> =>
    requestJson<PlaygroundRunResponse>(
      '/api/character-onboarding/image-generation',
      { method: 'POST', body: data },
      'Failed to start card generation'
    ),

  selectCharacterCard: async (data: CharacterCardSelectionRequest): Promise<CharacterCardSelectionResponse> =>
    requestJson<CharacterCardSelectionResponse>(
      '/api/character-onboarding/image-generation/select',
      { method: 'POST', body: data },
      'Failed to use the generated card'
    ),

  draftCharacterPrompt: async (data: CharacterPromptHelperRequest): Promise<CharacterPromptHelperResponse> =>
    requestJson<CharacterPromptHelperResponse>(
      '/api/character-onboarding/prompt-helper',
      { method: 'POST', body: data },
      'Failed to draft the character prompt'
    ),

  // Get presigned upload URL for admin reference image (or a dynamic character's scale reference)
  getAdminPresignedUploadUrl: async (
    character: Character,
    data: { contentType: string; fileSize: number; kind?: 'reference' | 'scale' | 'character-sheet' }
  ): Promise<{ uploadUrl: string; key: string; expiresIn: number }> =>
    requestJson<{ uploadUrl: string; key: string; expiresIn: number }>(
      `/api/admin/config/${character}/reference/upload`,
      { method: 'POST', body: data },
      'Failed to get upload URL'
    ),

  getRunwayCredits: (signal?: AbortSignal) =>
    requestJson<RunwayCreditsResponse>('/api/runway/credits', { signal }, 'Failed to fetch Runway credits'),

  createVideoDraft: async (data: CreateVideoDraftRequest): Promise<CreateVideoDraftResponse> =>
    requestJson<CreateVideoDraftResponse>(
      '/api/video-generations/drafts',
      { method: 'POST', body: data },
      'Failed to create video draft'
    ),

  uploadVideoInput: async (uploadUrl: string, file: File, input: VideoInputDescriptor): Promise<void> => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': input.contentType,
        'x-amz-meta-inputid': input.inputId,
        'x-amz-meta-sha256': input.sha256
      },
      body: file
    })
    if (!response.ok) {
      const responseBody = await response.text()
      const s3ErrorCode = responseBody.match(/<Code>([^<]+)<\/Code>/)?.[1]
      throw createAppError(
        response.status === 403
          ? 'The video upload link expired or was rejected. Select the media again and retry.'
          : 'The video input could not be uploaded. Check your connection and try again.',
        { code: s3ErrorCode === 'EntityTooLarge' ? 'UPLOAD_TOO_LARGE' : 'UPLOAD_FAILED', status: response.status }
      )
    }
  },

  submitVideoGeneration: async (generationId: string): Promise<{ generation: VideoGeneration }> =>
    requestJson<{ generation: VideoGeneration }>(
      `/api/video-generations/${generationId}/submit`,
      { method: 'POST' },
      'Failed to submit video generation'
    ),

  getVideoGeneration: async (generationId: string): Promise<VideoGeneration> => {
    const response = await requestJson<VideoGeneration>(
      `/api/video-generations/${generationId}`,
      undefined,
      'Failed to fetch video generation'
    )
    requireReadableGeneration(typeof response?.generationId === 'string' && typeof response.status === 'string')
    return response
  },

  getVideoGenerations: async (params: {
    creator?: string
    limit?: number
    cursor?: string
    status?: VideoGenerationStatus
  }): Promise<VideoGenerationHistoryResponse> => {
    const query = new URLSearchParams()
    if (params.creator) query.set('creator', params.creator)
    if (params.limit) query.set('limit', String(params.limit))
    if (params.cursor) query.set('cursor', params.cursor)
    if (params.status) query.set('status', params.status)
    return requestJson<VideoGenerationHistoryResponse>(
      `/api/video-generations?${query.toString()}`,
      undefined,
      'Failed to fetch video history'
    )
  },

  getVideoMediaUrl: async (
    generationId: string,
    disposition: 'inline' | 'attachment',
    inputId?: string
  ): Promise<{ url: string; expiresAt: string }> =>
    requestJson<{ url: string; expiresAt: string }>(
      `/api/video-generations/${generationId}/media-url`,
      { method: 'POST', body: { disposition, ...(inputId ? { inputId } : {}) } },
      'Failed to get video URL'
    ),

  deleteVideoGeneration: async (generationId: string): Promise<void> =>
    requestVoid(`/api/video-generations/${generationId}`, { method: 'DELETE' }, 'Failed to delete video generation')
}

export { API_URL }
